import React, { useRef, useState } from "react";
import { SvgComponent } from "@commons/Componentes/SvgComponent/SvgComponent";

const Etiqueta = ({ tono, icono, titulo, children }) => (
  <span className={`citrus-gpt-tag citrus-gpt-tag--${tono}`} title={titulo} tabIndex={titulo ? 0 : undefined}>
    {icono && <SvgComponent iconName={icono} width="13px" height="13px" fill="currentColor" />}
    {children}
  </span>
);

const urlFuenteSegura = valor => {
  if (typeof valor !== "string" || /[\uE200\uE201]/.test(valor)) return null;
  try {
    const url = new URL(valor);
    return (url.protocol === "https:" || url.protocol === "http:") && url.hostname && !url.username && !url.password ? valor : null;
  } catch (_) { return null; }
};

const FuentesNative = ({ fuentes }) => {
  const utilizadas = (fuentes || []).filter(fuente => typeof fuente?.Titulo === "string" && fuente.Titulo.trim());
  if (!utilizadas.length) return null;
  return <details className="citrus-gpt-native__fuentes">
    <summary>Fuentes ({utilizadas.length})</summary>
    <ul>{utilizadas.map((fuente, i) => {
      const url = urlFuenteSegura(fuente.Url);
      const citas = (fuente.Citas || []).filter(cita => /^(CIT|DGII|CF)-[1-9][0-9]*$/.test(cita?.Etiqueta || ""));
      return <li key={i}>
        {citas.length > 0 && <strong>{[...new Set(citas.map(cita => `[${cita.Etiqueta}]`))].join(" ")} </strong>}
        {url ? <a href={url} target="_blank" rel="noopener noreferrer">{fuente.Titulo}</a> : fuente.Titulo}
        {fuente.NaturalezaFuente === "citrus_derivado" && <div>Ficha derivada</div>}
      </li>;
    })}</ul>
  </details>;
};

const ObservacionNative = ({ resultado, onGuardar }) => {
  const [texto, setTexto] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState("");
  const [intento, setIntento] = useState(null);
  const ocupado = useRef(false);
  const limite = 2000;
  const guardar = async () => {
    const observacion = texto;
    if (!observacion.trim() || observacion.length > limite || ocupado.current || !onGuardar) return;
    ocupado.current = true; setGuardando(true); setAviso("");
    try {
      let feedbackId = intento?.texto === observacion ? intento.id : null;
      if (!feedbackId) {
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        feedbackId = Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
        setIntento({ id: feedbackId, texto: observacion });
      }
      const datos = await onGuardar(resultado.RequestId, observacion, feedbackId);
      if (datos?.Guardada !== true) throw { Mensaje: "No se confirmó el registro de la observación. Conserve el texto." };
      setTexto(""); setIntento(null);
      setAviso("Observación registrada para revisión. No valida la respuesta ni se incorpora como conocimiento.");
    } catch (error) {
      setAviso(error?.Mensaje || "No se confirmó el registro de la observación. La respuesta permanece disponible.");
    } finally { ocupado.current = false; setGuardando(false); }
  };
  return <section className="citrus-gpt-native__observacion">
    <strong>Sus observaciones</strong>
    <p>Puede comentar la respuesta o señalar qué necesita revisar. Este comentario no aprueba la respuesta ni alimenta el conocimiento del asistente.</p>
    {(resultado.ObservacionesUsuario || []).map((o, i) => <blockquote key={o.Id || i}>{o.Texto}</blockquote>)}
    {onGuardar && <>
      <label>Observación para revisión (máximo {limite} caracteres)
        <textarea value={texto} onChange={event => setTexto(event.target.value)} disabled={guardando} rows={3} />
      </label>
      <button type="button" className="citrus-gpt-btn" onClick={guardar} disabled={guardando || !texto.trim() || texto.length > limite}>
        {guardando ? "Registrando observación…" : "Registrar observación"}
      </button>
    </>}
    {aviso && <p role="status">{aviso}</p>}
  </section>;
};

/**
 * Un turno del chat. El contenido llega como HTML ya saneado desde el servidor:
 * MarkdownHelper escapa antes de dar formato, así que el modelo no puede inyectar marcado.
 */
export const ChatMensaje = ({ mensaje, puedeRevisar, onGuardarObservacion }) => {
  const respuesta = mensaje?.Respuesta;
  const esAsistente = Boolean(mensaje?.EsAsistente);
  // Native.ContenidoHtml is the server's single public projection of the principal's answer.
  // Internal contributions and the older message body must not replace or repeat it.
  const native = esAsistente ? mensaje?.Native : null;
  const contenidoHtml = native ? native.ContenidoHtml || "" : mensaje?.ContenidoHtml || "";

  return (
    <div className={`citrus-gpt-msg citrus-gpt-msg--${esAsistente ? "asistente" : "usuario"}`}>
      <span className="citrus-gpt-msg__remitente">{mensaje?.Remitente}</span>

      {mensaje?.TextoNative != null && !native ? <div className="citrus-gpt-msg__burbuja">{mensaje.TextoNative}</div> : <div
        className={`citrus-gpt-msg__burbuja${native ? " citrus-gpt-msg__burbuja--native" : ""}`}
        dangerouslySetInnerHTML={{ __html: contenidoHtml }}
      />}

      {native && <FuentesNative fuentes={native.Fuentes} />}
      {esAsistente && mensaje?.Native?.RequestId && <ObservacionNative resultado={mensaje.Native} onGuardar={onGuardarObservacion} />}

      {esAsistente && respuesta && !mensaje?.Native && (
        <>
          <div className="citrus-gpt-msg__meta">
            {respuesta.EsValidada ? (
              <Etiqueta
                tono="validada"
                icono="check"
                titulo="Un experto revisó esta respuesta y la dio por buena."
              >
                Validada{respuesta.ValidadaPor ? ` por ${respuesta.ValidadaPor}` : ""}
              </Etiqueta>
            ) : (
              <Etiqueta
                tono="pendiente"
                titulo="Todavía ningún experto la ha revisado. Verifíquela antes de aplicarla."
              >
                Sin validar
              </Etiqueta>
            )}

            {respuesta.EsRiesgoAlto && (
              <Etiqueta
                tono="riesgo"
                icono="alert"
                titulo="Aplicar esta respuesta podría tener consecuencias importantes si es incorrecta, por ejemplo en contabilidad, inventario o cierres. No significa que esté equivocada; un experto debe validarla antes de aplicar los pasos."
              >
                Riesgo alto
              </Etiqueta>
            )}

            {puedeRevisar && respuesta.Confianza != null && (
              <Etiqueta
                tono="neutra"
                titulo="Qué tan cerca estuvo la documentación recuperada de la pregunta."
              >
                Coincidencia {Number(respuesta.Confianza).toFixed(0)}%
              </Etiqueta>
            )}
          </div>

          {respuesta.EvidenciaInsuficiente && (
            <p className="citrus-gpt-msg__aviso">
              Esta respuesta no tiene respaldo suficiente en la documentación. Valídela con un experto.
            </p>
          )}

          {puedeRevisar && respuesta.Fuentes?.length > 0 && (
            <p className="citrus-gpt-msg__fuentes">
              <strong>Fuentes:</strong> {respuesta.Fuentes.join(", ")}
            </p>
          )}

          {puedeRevisar && respuesta.IdSec > 0 && (
            <a
              className="citrus-gpt-msg__revisar"
              href={`/App/Seguridad/ConocimientoIAEdit?IdSec=${respuesta.IdSec}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Revisar esta respuesta
            </a>
          )}
        </>
      )}
    </div>
  );
};
